import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useMemo, useState } from 'react';
import { CreditCard, AlertTriangle, X, Check } from 'lucide-react';
import { money, type DeviceBundleRow, type DeviceModelRow } from '../../services/hardwareApi';
import { billingApi, type BillingSkuRow, type FinanceAgreementRow } from '../../services/billingApi';
import { RUBY, RUBY_DEEP } from './InventoryChrome';

/**
 * THE AGREEMENT BOOK — every live device finance, lease and rental, derived
 * from Stripe rather than stored.
 *
 * EXTRACTED FROM `FinancingPage` (2026-08-30) when Stock and Financing became
 * one screen. Nothing about it changed in the move: the same five totals, the
 * same row-level table, and the same custom cancel-rental modal (deposit,
 * payback, minimum commitment, audited reason — never a native confirm). It
 * lives in its own file only so the merged Equipment & Financing screen and
 * anything else that needs the book render the SAME component instead of two
 * that drift.
 *
 * Payback = rent collected vs the device's landed cost, joined client-side
 * through the SKU's modelId/bundleId. A derived convenience figure, never
 * stored; rows whose SKU no longer resolves show no payback rather than a
 * guessed one.
 */

const STATUS_CHIP: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-amber-100 text-amber-700',
  canceled: 'bg-gray-200 text-gray-600',
  unpaid: 'bg-red-100 text-red-700',
};

const isRental = (r: FinanceAgreementRow) => r.financeType === 'rental';

export interface FinancingBookProps {
  rows: FinanceAgreementRow[];
  skus: BillingSkuRow[];
  models: DeviceModelRow[];
  bundles: DeviceBundleRow[];
  generatedAt: number | null;
  /** Re-read after a cancellation lands. */
  onChanged: () => Promise<void> | void;
  /** Hides the five totals when the host screen already shows them. */
  showTotals?: boolean;
  /** Present when the server has more rows; renders the Load more control. */
  nextToken?: string | null;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /**
   * WHOLE-BOOK totals from the server. When given, the tiles show the whole
   * book rather than the page currently loaded — which is the only correct
   * behaviour once the rows are paged. Absent falls back to computing from
   * `rows`, which is right for the pre-ledger fallback response.
   */
  serverTotals?: {
    agreements: number; totalMonthlyCents: number; totalOutstandingCents: number;
    activeRentals: number; rentalsPastPayback: number; depositsHeldCents: number;
  };
}

/** The five totals, on their own so a host can place them in its own stat strip. */
export const financingTotals = (
  rows: FinanceAgreementRow[],
  costForRow: (r: FinanceAgreementRow) => number | null,
) => {
  const liveRows = rows.filter((r) => r.subscriptionStatus !== 'canceled');
  const activeRentals = liveRows.filter((r) => isRental(r) && !r.cancelAtPeriodEnd);
  return {
    agreements: rows.length,
    totalMonthly: liveRows.reduce((s, r) => s + r.monthlyCents, 0),
    totalOutstanding: liveRows.reduce((s, r) => s + (r.remainingCents ?? 0), 0),
    activeRentals: activeRentals.length,
    rentalsPastPayback: activeRentals.filter((r) => {
      const cost = costForRow(r);
      return cost !== null && r.paidCents >= cost;
    }).length,
    depositsHeldCents: liveRows
      .filter(isRental)
      .reduce((s, r) => s + (r.depositCents || 0) * (r.quantity || 1), 0),
  };
};

/**
 * Landed cost behind one row's devices, or null when unresolvable. Exported so
 * a host screen can build its own totals from the same join rather than a
 * second one.
 */
export const useCostForRow = (
  skus: BillingSkuRow[],
  models: DeviceModelRow[],
  bundles: DeviceBundleRow[],
) => useMemo(() => {
  const skuById = new Map(skus.map((s) => [s.skuId, s]));
  const modelById = new Map(models.map((m) => [m.modelId, m]));
  const bundleById = new Map(bundles.map((b) => [b.bundleId, b]));
  return (r: FinanceAgreementRow): number | null => {
    // The ledger snapshots landed cost at SALE time. Prefer it: the live join
    // below describes what an identical device costs TODAY, which is not what
    // payback means. The join remains for the pre-ledger fallback response.
    if (typeof r.costCents === 'number' && r.costCents > 0) {
      return r.costCents * (r.quantity || 1);
    }
    const sku = skuById.get(r.skuId);
    if (!sku) return null;
    const unitCost = sku.bundleId
      ? bundleById.get(sku.bundleId)?.totalCostCents
      : sku.modelId ? modelById.get(sku.modelId)?.landedCostCents : undefined;
    if (typeof unitCost !== 'number' || unitCost <= 0) return null;
    return unitCost * (r.quantity || 1);
  };
}, [skus, models, bundles]);

export const FinancingStatTiles: React.FC<{
  rows: FinanceAgreementRow[];
  costForRow: (r: FinanceAgreementRow) => number | null;
  /** Whole-book figures. Preferred over deriving from `rows`, which once the
   *  book is paged only ever describes the page currently loaded. */
  serverTotals?: FinancingBookProps['serverTotals'];
}> = ({ rows, costForRow, serverTotals }) => {
  const t = serverTotals
    ? {
        agreements: serverTotals.agreements,
        totalMonthly: serverTotals.totalMonthlyCents,
        totalOutstanding: serverTotals.totalOutstandingCents,
        activeRentals: serverTotals.activeRentals,
        rentalsPastPayback: serverTotals.rentalsPastPayback,
        depositsHeldCents: serverTotals.depositsHeldCents,
      }
    : financingTotals(rows, costForRow);
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="text-2xl font-light text-stone-900">{t.agreements}</div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-stone-400 mt-1">Agreements</div>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="text-2xl font-light text-stone-900">{money(t.totalMonthly)}</div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-stone-400 mt-1">Monthly hardware revenue</div>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="text-2xl font-light text-stone-900">{money(t.totalOutstanding)}</div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-stone-400 mt-1">Outstanding (financed)</div>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="text-2xl font-light text-stone-900">
          {t.activeRentals}
          {t.rentalsPastPayback > 0 && (
            <span className="ml-1.5 align-middle text-xs font-medium text-teal-600">· {t.rentalsPastPayback} past payback</span>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-stone-400 mt-1">Active rentals</div>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="text-2xl font-light text-stone-900">{money(t.depositsHeldCents)}</div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-stone-400 mt-1">Deposits held</div>
      </div>
    </div>
  );
};

const FinancingBook: React.FC<FinancingBookProps> = ({
  rows, skus, models, bundles, generatedAt, onChanged, showTotals = true,
  nextToken, loadingMore, onLoadMore, serverTotals,
}) => {
  // Cancel-rental modal (custom — never a native confirm).
  const [cancelTarget, setCancelTarget] = useState<FinanceAgreementRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelDone, setCancelDone] = useState<string | null>(null);

  const costForRow = useCostForRow(skus, models, bundles);

  const submitCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const res = await billingApi.cancelFinancing({
        subscriptionId: cancelTarget.subscriptionId,
        skuId: cancelTarget.skuId,
        reason: cancelReason.trim(),
      });
      setCancelDone(
        `Rental cancelled — billing stops ${res.currentPeriodEndAt ? new Date(res.currentPeriodEndAt).toLocaleDateString() : 'at period end'}. `
        + `${res.unitsCancelled} unit${res.unitsCancelled === 1 ? '' : 's'} flagged for return`
        + (res.unitsMatched === 0 ? ' (no unit carries this agreement yet — nothing to flag)' : '')
        + '.',
      );
      setCancelTarget(null);
      setCancelReason('');
      await onChanged();
    } catch (e: any) {
      setCancelError(e?.message || 'Failed to cancel the rental');
    } finally {
      setCancelling(false);
    }
  };

  const progressCell = (r: FinanceAgreementRow) => {
    if (isRental(r) && (r.rentalKind ?? 'open_ended') === 'open_ended') {
      return (
        <span className="font-mono">
          {r.monthsPaid} mo · <span className="text-[9px] font-bold text-teal-600">ONGOING</span>
          {(r.minCommitmentMonths || 0) > 0 && (
            <span className="text-stone-400"> · min {r.minCommitmentMonths}</span>
          )}
        </span>
      );
    }
    return (
      <span className="font-mono">
        {r.monthsPaid}/{r.termMonths ?? '—'} mo
        {isRental(r) && <span className="text-[9px] text-stone-400"> (fixed)</span>}
      </span>
    );
  };

  const paybackCell = (r: FinanceAgreementRow) => {
    const cost = costForRow(r);
    if (cost === null) return <span className="text-stone-300">—</span>;
    const pct = Math.min(100, Math.round((r.paidCents / cost) * 100));
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-1 w-14 overflow-hidden rounded-full bg-stone-200 align-middle">
          <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#10b981,#34d399)' }} />
        </span>
        <span className={`font-mono ${pct >= 100 ? 'text-emerald-600' : 'text-stone-600'}`}>
          {pct >= 100 ? 'paid back' : `${pct}%`}
        </span>
      </span>
    );
  };

  return (
    <>
      {cancelDone && (
        <div className="mb-4 flex items-start justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <span className="flex items-start gap-2"><Check size={15} className="mt-0.5 shrink-0" /> {cancelDone}</span>
          <button onClick={() => setCancelDone(null)} className="shrink-0 text-emerald-700"><X size={14} /></button>
        </div>
      )}

      {showTotals && (
        <div className="mb-5">
          <FinancingStatTiles rows={rows} costForRow={costForRow} serverTotals={serverTotals} />
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <CreditCard size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">No financed devices yet</p>
          <p className="mt-1 text-xs text-stone-500">Give a device selling terms, then a tenant&apos;s onboarding order needs to select them.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wider text-stone-400">
                <th className="text-left px-4 py-3">Tenant</th>
                <th className="text-left px-4 py-3">Offer</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-right px-4 py-3">Monthly</th>
                <th className="text-left px-4 py-3">Progress</th>
                <th className="text-right px-4 py-3">Remaining</th>
                <th className="text-left px-4 py-3">Deposit</th>
                <th className="text-left px-4 py-3">Payback</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.subscriptionId}-${r.skuId}-${i}`} className="border-b border-stone-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-stone-800">{r.tenantName || r.tenantId || '— unattributed —'}</td>
                  <td className="px-4 py-3 text-stone-600">{r.name}{r.quantity > 1 ? ` ×${r.quantity}` : ''}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                      isRental(r) ? 'bg-teal-100 text-teal-700' : 'bg-rose-100 text-rose-700'
                    }`}>
                      {isRental(r) ? 'RENT' : r.financeType === 'lease' ? 'LEASE' : 'FINANCE'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-stone-800">{money(r.monthlyCents)}</td>
                  <td className="px-4 py-3 text-stone-800">{progressCell(r)}</td>
                  <td className="px-4 py-3 text-right font-mono text-stone-900">{r.remainingCents === null ? '—' : money(r.remainingCents)}</td>
                  <td className="px-4 py-3">
                    {(r.depositCents || 0) > 0 ? (
                      <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 whitespace-nowrap">
                        {money((r.depositCents || 0) * (r.quantity || 1))} held
                      </span>
                    ) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-4 py-3">{paybackCell(r)}</td>
                  <td className="px-4 py-3">
                    {r.cancelAtPeriodEnd && r.subscriptionStatus !== 'canceled' ? (
                      <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
                        ending {r.currentPeriodEndAt ? new Date(r.currentPeriodEndAt).toLocaleDateString() : 'at period end'}
                      </span>
                    ) : (
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_CHIP[r.subscriptionStatus] || 'bg-gray-100 text-gray-600'}`}>
                        {r.subscriptionStatus}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isRental(r) && !r.cancelAtPeriodEnd && r.subscriptionStatus !== 'canceled' && (
                      <button
                        onClick={() => { setCancelTarget(r); setCancelReason(''); setCancelError(null); }}
                        className="rounded-lg border border-stone-300 px-2.5 py-1 text-[10.5px] font-semibold text-stone-600 hover:border-red-300 hover:text-red-600"
                      >
                        Cancel…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextToken && onLoadMore && (
        <div className="border-t border-stone-100 py-3 text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-[11px] font-semibold text-stone-700 disabled:opacity-50 min-h-[44px]"
          >
            {loadingMore ? <span className="inline-flex"><RubyGemMark size={13} drawing color="currentColor" /></span> : 'Load more agreements'}
          </button>
        </div>
      )}

      <p className="mt-3 text-[11px] text-stone-500">
        Derived live from Stripe{generatedAt ? ` as of ${new Date(generatedAt).toLocaleTimeString()}` : ''}. Open-ended
        rentals show months paid with no denominator — they bill until cancelled. Payback = rent collected vs the
        device&apos;s landed cost, never elapsed time.
      </p>

      {/* Cancel rental — custom modal, never a native confirm. */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !cancelling && setCancelTarget(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-stone-900">
                Cancel rental — {cancelTarget.tenantName || cancelTarget.tenantId || 'unattributed'}
              </h3>
              <button onClick={() => !cancelling && setCancelTarget(null)} className="text-stone-400 hover:text-stone-600"><X size={16} /></button>
            </div>
            <p className="mb-4 text-[11px] text-stone-500">
              {cancelTarget.name}{cancelTarget.quantity > 1 ? ` ×${cancelTarget.quantity}` : ''} · renting {cancelTarget.monthsPaid} month{cancelTarget.monthsPaid === 1 ? '' : 's'} at {money(cancelTarget.monthlyCents)}/mo
            </p>

            <div className="divide-y divide-stone-100 rounded-xl border border-stone-200 text-[11.5px]">
              <div className="flex justify-between px-3 py-2"><span className="text-stone-500">Rent collected to date</span><span className="font-mono text-stone-900">{money(cancelTarget.paidCents)} ({cancelTarget.monthsPaid} payments)</span></div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-stone-500">Device cost recovery</span>
                <span className="font-mono text-stone-900">{(() => {
                  const cost = costForRow(cancelTarget);
                  if (cost === null) return 'unknown';
                  return cancelTarget.paidCents >= cost ? `${money(cost)} ✓ recovered` : `${money(cancelTarget.paidCents)} of ${money(cost)}`;
                })()}</span>
              </div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-stone-500">Minimum commitment</span>
                <span className="font-mono text-stone-900">
                  {(cancelTarget.minCommitmentMonths || 0) > 0
                    ? `${cancelTarget.minCommitmentMonths} mo — ${cancelTarget.monthsPaid >= (cancelTarget.minCommitmentMonths || 0) ? 'met' : 'NOT met'}`
                    : 'none'}
                </span>
              </div>
              <div className="flex justify-between px-3 py-2"><span className="text-stone-500">Security deposit held</span><span className="font-mono text-stone-900">{(cancelTarget.depositCents || 0) > 0 ? money((cancelTarget.depositCents || 0) * (cancelTarget.quantity || 1)) : 'none'}</span></div>
            </div>

            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[10.5px] leading-relaxed text-amber-900">
              Stripe billing stops at the period end{cancelTarget.currentPeriodEndAt ? ` (${new Date(cancelTarget.currentPeriodEndAt).toLocaleDateString()})` : ''} — the tenant keeps what they paid for.
              The device must come back: its unit is flagged <b>return expected</b> and is received through catalog check-in,
              where the deposit is settled after inspection.
            </div>

            <label className="mt-3 mb-1 block text-[11px] font-semibold text-stone-600">Reason (audited, required)</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
              placeholder="e.g. tenant closing location, returning device"
              className="w-full rounded-lg border border-stone-300 px-2.5 py-2 text-xs text-stone-900"
            />

            {cancelError && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-[11px] text-red-800">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {cancelError}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                className="rounded-lg border border-stone-300 px-3.5 py-2 text-xs font-semibold text-stone-600"
              >
                Keep renting
              </button>
              <button
                onClick={submitCancel}
                disabled={cancelling || !cancelReason.trim()}
                className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}
              >
                {cancelling ? <RubyGemMark size={13} drawing color="currentColor" /> : null}
                Cancel rental &amp; expect return
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FinancingBook;
